import { useContext, useEffect } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { Routes } from 'types/routes';
import { Section } from 'types/sections';

import CmsSection from 'models/CmsSection';

import LocaleContext from 'components/context/Locale';
import MarketContext from 'components/context/Market';
import Breadcrumbs from 'components/layout/Breadcrumbs/component';
import Head from 'components/layout/Head';
import PageContainer from 'components/layout/PageContainer';
import SectionsWrapper from 'components/modules/public/cms/SectionsWrapper';
import SectionWrapper from 'components/modules/public/cms/SectionWrapper';
import WrapperPublic from 'components/wrappers/Public';

import SectionCheckout from './sections/Checkout';
import SectionContact from './sections/Contact';
import SectionHero from './sections/Hero';
import SectionLocationCities from './sections/LocationCities';
import SectionMap from './sections/Map';
import StyledComponent from './styles';
import { Props } from './types';

//TODO: MID - Use routes from translations
const PublicPageLocations: NextPage<Props> = ({ locations, cmsPage }) => {
    const localeContext = useContext(LocaleContext);
    const { productCategories } = useContext(MarketContext);
    const translations = localeContext?.translations?.locations;
    const translationsBreadcrumbs = localeContext?.translations?.breadcrumbs;
    const router = useRouter();
    const currentProductCategory = productCategories.find((category) => category?.locales[0]?.slug === router.query.categorySlug);
    const currentProductCategoryId = currentProductCategory?.id;

    useEffect(() => {
        if (currentProductCategoryId && typeof currentProductCategoryId === 'string') {
            setTimeout(() => {
                const scrollWithOffset = (element: HTMLElement | null) => {
                    if (!element) return;

                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

                    const offsetPosition = scrollTop + rect.top - 150;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                    });
                };

                const sectionElement = document.getElementById(currentProductCategoryId);
                if (sectionElement) {
                    scrollWithOffset(sectionElement);
                    return;
                }
            }, 500);
        }
    }, [router.query]);

    const breadcrumbs = [{
        to: Routes.PublicHome,
        label: translationsBreadcrumbs.home,
    }, {
        to: Routes.PublicLocations,
        label: translationsBreadcrumbs.locations,
        active: true,
    }];

    const sections: Section[] = [
        {
            slug: 'hero',
            order: 1,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionHero
                            translations={{
                                ...translations?.content?.hero,
                                ...locale,
                            }}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'breadcrumbs',
            order: 2,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (<Breadcrumbs breadcrumbs={breadcrumbs} />)}
                </SectionWrapper>
            ),
        },
        {
            slug: 'mapEpilation',
            order: 3,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionMap
                            translations={{
                                ...translations?.content?.map,
                                ...locale,
                            }}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'locationCitiesEpilation',
            order: 4,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionLocationCities
                            override={() => override({
                                locations: locations,
                            })}
                            locale={locale}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'mapEndermology',
            order: 5,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionMap
                            translations={{
                                ...translations?.content?.map,
                                ...locale,
                            }}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'locationCitiesEndermology',
            order: 6,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionLocationCities
                            override={() => override({
                                locations: locations,
                            })}
                            locale={locale}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'mapEmSpec',
            order: 7,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionMap
                            translations={{
                                ...translations?.content?.map,
                                ...locale,
                            }}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'locationCitiesEmSpec',
            order: 8,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionLocationCities
                            override={() => override({
                                locations: locations,
                            })}
                            locale={locale}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'mapHydrogen',
            order: 9,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionMap
                            translations={{
                                ...translations?.content?.map,
                                ...locale,
                            }}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'locationCitiesHydrogen',
            order: 10,
            visible: Array.isArray(locations) && locations.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionLocationCities
                            override={() => override({
                                locations: locations,
                            })}
                            locale={locale}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'contact',
            order: 11,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionContact
                            locale={locale}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'checkout',
            order: 12,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionCheckout
                            translations={{
                                ...translations?.content?.checkout,
                                ...locale,
                            }}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
    ];

    return (
        <WrapperPublic
            isBannerVisible={cmsPage?.data?.isBannerVisible}
            bannerContent={cmsPage?.data?.bannerContent}
            bannerLink={cmsPage?.data?.bannerLink}
        >
            <StyledComponent className="public-page-locations">
                <Head
                    title={cmsPage?.data?.locale?.title || translations?.meta?.title}
                    description={cmsPage?.data?.locale?.description || translations?.meta?.description}
                    keywords={cmsPage?.data?.locale?.keywords || translations?.meta?.keywords}
                    tags={cmsPage?.data?.head}
                    cmsPageIndexingEnabled={cmsPage?.data?.indexingEnabled}
                />
                <PageContainer>
                    <SectionsWrapper
                        sections={sections}
                        cmsSections={cmsPage?.data?.sections}
                    />
                </PageContainer>
            </StyledComponent>
        </WrapperPublic>
    );
};

export default PublicPageLocations;
